import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Shield, Search, Plus, Pencil, Trash2, UserPlus, Clock, AlertTriangle,
  CheckCircle2, Users, BookOpen, ArrowRight, Library, Loader2, ChevronRight,
  Filter, TrendingUp, Target, XCircle, Building2, RefreshCw, Share2
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Progress } from "../../components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import AssignmentModal from "../../components/shared/AssignmentModal";
import ShareDialog from "../../components/shared/ShareDialog";
import ConfirmDeleteDialog from "../../components/shared/ConfirmDeleteDialog";

// ── Compliance Page ──
const CompliancePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const basePath = user?.role === "super_admin" ? "/admin" : "/tenant";
  const variant = user?.role === "super_admin" ? "super_admin" : "tenant_admin";

  // Data state
  const [templates, setTemplates] = useState([]);
  const [library, setLibrary] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI state
  const [activeTab, setActiveTab] = useState("active");
  const [search, setSearch] = useState("");
  const [showWizard, setShowWizard] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [wizardStep, setWizardStep] = useState(0);
  const [wizardData, setWizardData] = useState({ title: "", description: "", due_days: 30, is_mandatory: true, recurrence: "", course_ids: [], is_library: false, category: "Safety" });
  const [courseSearch, setCourseSearch] = useState("");
  const [showAssign, setShowAssign] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [activating, setActivating] = useState(null);
  const [shareTemplate, setShareTemplate] = useState(null);

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [tRes, lRes, aRes, dRes, cRes] = await Promise.all([
        api.get("/compliance/templates"),
        api.get("/compliance/library"),
        api.get("/compliance/assignments"),
        api.get("/compliance/dashboard"),
        api.get("/courses"),
      ]);
      setTemplates(tRes.data || []);
      setLibrary(lRes.data || []);
      setAssignments(aRes.data || []);
      setDashboard(dRes.data);
      const c = cRes.data?.courses || cRes.data || [];
      setCourses(Array.isArray(c) ? c : []);
    } catch (e) { toast.error("Failed to load compliance data"); }
    setLoading(false);
  };

  // ── Computed: enrich templates with assignment stats ──
  const enrichedTemplates = useMemo(() => {
    return templates.map(t => {
      const tAssigns = assignments.filter(a => a.template_id === t.id);
      const total = tAssigns.length;
      const completed = tAssigns.filter(a => a.status === "completed").length;
      const overdue = tAssigns.filter(a => a.status === "overdue").length;
      const pending = total - completed - overdue;
      const rate = total > 0 ? Math.round((completed / total) * 100) : 0;
      return { ...t, _total: total, _completed: completed, _overdue: overdue, _pending: pending, _rate: rate };
    });
  }, [templates, assignments]);

  // ── Filtered templates per tab ──
  const filteredTemplates = useMemo(() => {
    let list = enrichedTemplates;
    if (activeTab === "completed") list = list.filter(t => t._total > 0 && t._rate === 100);
    else if (activeTab === "overdue") list = list.filter(t => t._overdue > 0);
    else if (activeTab === "active") list = list.filter(t => t._total > 0 && t._rate < 100);

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(t => t.title.toLowerCase().includes(q) || (t.description || "").toLowerCase().includes(q) || (t.category || "").toLowerCase().includes(q));
    }
    return list;
  }, [enrichedTemplates, activeTab, search]);

  const filteredLibrary = useMemo(() => {
    if (!search.trim()) return library;
    const q = search.toLowerCase();
    return library.filter(t => t.title.toLowerCase().includes(q) || (t.description || "").toLowerCase().includes(q));
  }, [library, search]);

  const activatedIds = useMemo(() => new Set(templates.map(t => t.source_library_id).filter(Boolean)), [templates]);

  // ── Handlers ──
  const handleCreate = async () => {
    try {
      const payload = { ...wizardData, recurrence: wizardData.recurrence || null };
      if (editTemplate) {
        await api.put(`/compliance/templates/${editTemplate.id}`, payload);
        toast.success("Compliance updated");
      } else {
        await api.post("/compliance/templates", payload);
        toast.success("Compliance created");
      }
      setShowWizard(false);
      resetWizard();
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/compliance/templates/${id}`);
      toast.success("Deleted");
      setShowDeleteConfirm(null);
      fetchAll();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const handleActivateLibrary = async (libId) => {
    setActivating(libId);
    try {
      const res = await api.post(`/compliance/library/${libId}/activate`);
      toast.success(res.data?.message || "Activated");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to activate");
    }
    setActivating(null);
  };

  const resetWizard = () => {
    setWizardData({ title: "", description: "", due_days: 30, is_mandatory: true, recurrence: "", course_ids: [], is_library: false, category: "Safety" });
    setWizardStep(0);
    setEditTemplate(null);
  };

  const openEdit = (t) => {
    setEditTemplate(t);
    setWizardData({
      title: t.title, description: t.description || "", due_days: t.due_days || 30,
      is_mandatory: t.is_mandatory, recurrence: t.recurrence || "",
      course_ids: t.course_ids || [], is_library: t.is_library || false, category: t.category || "Safety"
    });
    setWizardStep(0);
    setShowWizard(true);
  };

  if (loading) return (
    <AdminLayout title="Compliances" variant={variant}>
      <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
    </AdminLayout>
  );

  const d = dashboard || { total: 0, completed: 0, overdue: 0, pending: 0, completion_rate: 0 };
  const deptStats = d.department_stats || {};

  return (
    <AdminLayout title="Compliances" variant={variant}>
      <div className="space-y-5" data-testid="compliance-page">

        {/* ── Overall Compliance Progress Dashboard ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Main Stats */}
          <Card className="border shadow-none lg:col-span-2">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-bold tracking-tight">Organization Compliance</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">Overall compliance health across all programs</p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-black text-primary">{d.completion_rate}%</p>
                  <p className="text-[10px] text-muted-foreground">Completion Rate</p>
                </div>
              </div>
              <Progress value={d.completion_rate} className="h-3 mb-3" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
                {[
                  { label: "Total Assigned", value: d.total, icon: Users, color: "text-blue-600", bg: "bg-blue-50" },
                  { label: "Compliant", value: d.completed, icon: CheckCircle2, color: "text-green-600", bg: "bg-green-50" },
                  { label: "Pending", value: d.pending, icon: Clock, color: "text-amber-600", bg: "bg-amber-50" },
                  { label: "Overdue", value: d.overdue, icon: AlertTriangle, color: "text-red-600", bg: "bg-red-50" },
                ].map((st, i) => (
                  <div key={i} className={`flex items-center gap-2.5 p-2.5 rounded-lg ${st.bg}`}>
                    <st.icon className={`w-4 h-4 ${st.color} shrink-0`} />
                    <div>
                      <p className="text-lg font-bold leading-none">{st.value}</p>
                      <p className="text-[10px] text-muted-foreground">{st.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Department Breakdown */}
          <Card className="border shadow-none">
            <CardHeader className="pb-2 pt-4 px-5">
              <CardTitle className="text-sm flex items-center gap-2">
                <Building2 className="w-4 h-4 text-primary" /> By Department
              </CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-4 space-y-2.5">
              {Object.keys(deptStats).length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">No data yet</p>
              ) : Object.entries(deptStats).map(([dept, stats]) => {
                const dRate = stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0;
                return (
                  <div key={dept} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium truncate">{dept}</span>
                      <div className="flex items-center gap-2">
                        {stats.overdue > 0 && <Badge className="bg-red-50 text-red-600 text-[9px] px-1.5 py-0">{stats.overdue} overdue</Badge>}
                        <span className="text-xs font-bold">{dRate}%</span>
                      </div>
                    </div>
                    <Progress value={dRate} className="h-1.5" />
                    <p className="text-[10px] text-muted-foreground">{stats.completed}/{stats.total} compliant</p>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>

        {/* ── Per-Template Progress Cards ── */}
        {enrichedTemplates.filter(t => t._total > 0).length > 0 && (
          <Card className="border shadow-none">
            <CardHeader className="pb-2 pt-4 px-5">
              <CardTitle className="text-sm flex items-center gap-2">
                <Target className="w-4 h-4 text-primary" /> Program Progress
              </CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {enrichedTemplates.filter(t => t._total > 0).map(t => (
                  <div
                    key={t.id}
                    className={`p-3 rounded-lg border cursor-pointer transition-all hover:shadow-sm ${
                      t._rate === 100 ? "border-green-200 bg-green-50/30 hover:border-green-300" :
                      t._overdue > 0 ? "border-red-200 bg-red-50/30 hover:border-red-300" :
                      "hover:border-primary/30"
                    }`}
                    onClick={() => navigate(`${basePath}/compliances/${t.id}`)}
                    data-testid={`progress-card-${t.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      {t._rate === 100 ? <CheckCircle2 className="w-4 h-4 text-green-500" /> :
                       t._overdue > 0 ? <AlertTriangle className="w-4 h-4 text-red-500" /> :
                       <Shield className="w-4 h-4 text-primary" />}
                      <span className={`text-sm font-bold ${t._rate === 100 ? "text-green-600" : t._overdue > 0 ? "text-red-600" : "text-primary"}`}>{t._rate}%</span>
                    </div>
                    <p className="text-xs font-medium truncate">{t.title}</p>
                    <Progress value={t._rate} className="h-1 mt-2" />
                    <p className="text-[9px] text-muted-foreground mt-1">{t._completed}/{t._total} users</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── Tab Filters + Search + Actions ── */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
            <TabsList className="bg-muted/50 h-9">
              <TabsTrigger value="active" className="text-xs gap-1.5" data-testid="tab-active">
                <Shield className="w-3.5 h-3.5" /> Active
                <Badge variant="secondary" className="ml-1 text-[9px] px-1.5 py-0 h-4">{enrichedTemplates.filter(t => t._total > 0 && t._rate < 100).length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="completed" className="text-xs gap-1.5" data-testid="tab-completed">
                <CheckCircle2 className="w-3.5 h-3.5" /> Completed
                <Badge variant="secondary" className="ml-1 text-[9px] px-1.5 py-0 h-4">{enrichedTemplates.filter(t => t._total > 0 && t._rate === 100).length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="overdue" className="text-xs gap-1.5" data-testid="tab-overdue">
                <AlertTriangle className="w-3.5 h-3.5" /> Overdue
                <Badge variant="secondary" className="ml-1 text-[9px] px-1.5 py-0 h-4">{enrichedTemplates.filter(t => t._overdue > 0).length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="all" className="text-xs gap-1.5" data-testid="tab-all">
                <Filter className="w-3.5 h-3.5" /> All
                <Badge variant="secondary" className="ml-1 text-[9px] px-1.5 py-0 h-4">{enrichedTemplates.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="library" className="text-xs gap-1.5" data-testid="tab-library">
                <Library className="w-3.5 h-3.5" /> Library
                <Badge variant="secondary" className="ml-1 text-[9px] px-1.5 py-0 h-4">{library.length}</Badge>
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search compliances..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9 w-56 text-sm" data-testid="search-input" />
            </div>
            <Button onClick={() => { resetWizard(); setShowWizard(true); }} className="h-9 text-xs gap-1.5" data-testid="new-compliance-btn">
              <Plus className="w-4 h-4" /> New Compliance
            </Button>
          </div>
        </div>

        {/* ── Content based on tab ── */}
        {activeTab === "library" ? (
          /* ── Library Tab ── */
          <div className="space-y-3" data-testid="library-section">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold">Pre-Made Compliance Programs</h3>
                <p className="text-xs text-muted-foreground">Industry-standard programs ready to activate for your organization</p>
              </div>
              {user?.role === "super_admin" && (
                <Button size="sm" variant="outline" className="text-xs h-8" onClick={() => { resetWizard(); setWizardData(d => ({ ...d, is_library: true })); setShowWizard(true); }}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add to Library
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <AnimatePresence>
                {filteredLibrary.map(t => {
                  const isActivated = activatedIds.has(t.id);
                  return (
                    <motion.div key={t.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                      <Card className={`border shadow-none transition-all hover:shadow-sm ${isActivated ? "opacity-60 border-green-200" : "hover:border-primary/30"}`} data-testid={`lib-card-${t.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${isActivated ? "bg-green-100" : "bg-primary/10"}`}>
                              {isActivated ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : <Library className="w-5 h-5 text-primary" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h4 className="text-sm font-semibold truncate">{t.title}</h4>
                                {t.is_mandatory && <Badge className="bg-red-50 text-red-600 text-[9px] px-1.5 py-0 border-red-200">Mandatory</Badge>}
                              </div>
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{t.description}</p>
                              <div className="flex items-center gap-3 mt-2.5">
                                {t.category && <Badge variant="outline" className="text-[9px]">{t.category}</Badge>}
                                <span className="text-[10px] text-muted-foreground flex items-center gap-1"><BookOpen className="w-3 h-3" /> {(t.course_ids || []).length} courses</span>
                                <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> {t.due_days}d</span>
                                {t.recurrence && <span className="text-[10px] text-muted-foreground capitalize"><RefreshCw className="w-3 h-3 inline mr-0.5" />{t.recurrence.replace("_", " ")}</span>}
                              </div>
                            </div>
                          </div>
                          <div className="mt-3 pt-3 border-t flex justify-end">
                            {isActivated ? (
                              <Badge className="bg-green-50 text-green-600 border-green-200 text-xs">Already Active</Badge>
                            ) : (
                              <Button size="sm" className="text-xs h-8 gap-1" onClick={() => handleActivateLibrary(t.id)} disabled={activating === t.id} data-testid={`activate-${t.id}`}>
                                {activating === t.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
                                Activate for Org
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
              {filteredLibrary.length === 0 && (
                <div className="col-span-full text-center py-10">
                  <Library className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
                  <p className="text-sm text-muted-foreground">No library templates found</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* ── Active / Completed / Overdue / All Tabs ── */
          <div className="space-y-3" data-testid="compliance-list">
            {/* Recommended Compliance from Library */}
            {activeTab === "active" && library.filter(l => !activatedIds.has(l.id)).length > 0 && (
              <Card className="border-dashed border-2 border-primary/20 bg-primary/[0.02]" data-testid="recommended-compliance">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Library className="w-4 h-4 text-primary" />
                      <h3 className="text-sm font-semibold">Recommended Compliance Programs</h3>
                      <Badge variant="outline" className="text-[9px]">{library.filter(l => !activatedIds.has(l.id)).length} available</Badge>
                    </div>
                    <Button variant="link" size="sm" className="text-xs h-6" onClick={() => setActiveTab("library")}>View Library <ArrowRight className="w-3 h-3 ml-1" /></Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    {library.filter(l => !activatedIds.has(l.id)).slice(0, 3).map(t => (
                      <div key={t.id} className="flex items-center gap-3 p-2.5 rounded-lg border bg-white hover:shadow-sm transition-shadow">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <Library className="w-4 h-4 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold truncate">{t.title}</p>
                          <p className="text-[10px] text-muted-foreground">{(t.course_ids || []).length} courses · {t.due_days}d</p>
                        </div>
                        <Button size="sm" variant="outline" className="h-7 text-[10px] shrink-0" onClick={() => handleActivateLibrary(t.id)} disabled={activating === t.id} data-testid={`rec-activate-${t.id}`}>
                          {activating === t.id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Activate"}
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <AnimatePresence>
              {filteredTemplates.length === 0 ? (
                <div className="text-center py-12">
                  {activeTab === "completed" ? <CheckCircle2 className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" /> :
                   activeTab === "overdue" ? <AlertTriangle className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" /> :
                   <Shield className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />}
                  <p className="text-sm text-muted-foreground">
                    {activeTab === "completed" ? "No fully completed compliance programs yet" :
                     activeTab === "overdue" ? "No overdue compliances — great job!" :
                     activeTab === "active" ? "No active compliance programs. Create one or activate from the Library." :
                     "No compliance templates match your search"}
                  </p>
                </div>
              ) : filteredTemplates.map(t => (
                <motion.div key={t.id} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }}>
                  <Card
                    className={`border shadow-none cursor-pointer transition-all hover:shadow-sm group ${
                      t._rate === 100 ? "border-green-200 bg-green-50/20 hover:border-green-300" :
                      t._overdue > 0 ? "border-red-200 bg-red-50/10 hover:border-red-300" :
                      "hover:border-primary/30"
                    }`}
                    onClick={() => navigate(`${basePath}/compliances/${t.id}`)}
                    data-testid={`compliance-card-${t.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        {/* Status icon */}
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                          t._rate === 100 ? "bg-green-100" : t._overdue > 0 ? "bg-red-100" : "bg-primary/10"
                        }`}>
                          {t._rate === 100 ? <CheckCircle2 className="w-5 h-5 text-green-600" /> :
                           t._overdue > 0 ? <AlertTriangle className="w-5 h-5 text-red-500" /> :
                           <Shield className="w-5 h-5 text-primary" />}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <h4 className="text-sm font-semibold group-hover:text-primary transition-colors">{t.title}</h4>
                            {t.is_mandatory && <Badge className="bg-red-50 text-red-600 text-[9px] px-1.5 py-0 border-red-200">Mandatory</Badge>}
                            {t.category && <Badge variant="outline" className="text-[9px]">{t.category}</Badge>}
                            {t.recurrence && <Badge variant="outline" className="text-[9px] capitalize">{t.recurrence.replace("_", " ")}</Badge>}
                          </div>
                          <p className="text-xs text-muted-foreground line-clamp-1">{t.description}</p>
                          <div className="flex items-center gap-4 mt-2">
                            <span className="text-[10px] text-muted-foreground"><BookOpen className="w-3 h-3 inline mr-0.5" />{(t.course_ids || []).length} courses</span>
                            <span className="text-[10px] text-muted-foreground"><Clock className="w-3 h-3 inline mr-0.5" />{t.due_days}d</span>
                            <span className="text-[10px] text-muted-foreground"><Users className="w-3 h-3 inline mr-0.5" />{t._total} assigned</span>
                          </div>
                        </div>

                        {/* Progress ring + stats */}
                        <div className="flex items-center gap-4 shrink-0">
                          <div className="text-right hidden sm:block">
                            <div className="flex items-center gap-3 text-[10px]">
                              <span className="text-green-600 font-medium">{t._completed} done</span>
                              {t._pending > 0 && <span className="text-amber-600">{t._pending} pending</span>}
                              {t._overdue > 0 && <span className="text-red-600 font-medium">{t._overdue} overdue</span>}
                            </div>
                            <div className="w-32 mt-1.5">
                              <Progress value={t._rate} className="h-1.5" />
                            </div>
                          </div>

                          <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 ${
                            t._rate === 100 ? "border-green-400 text-green-600" :
                            t._overdue > 0 ? "border-red-300 text-red-600" :
                            "border-primary/30 text-primary"
                          }`}>
                            <span className="text-sm font-bold">{t._rate}%</span>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => setShareTemplate(t)} data-testid={`share-${t.id}`} title="Share">
                              <Share2 className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => setShowAssign(t)} data-testid={`assign-${t.id}`}>
                              <UserPlus className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => openEdit(t)}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-500" onClick={() => setShowDeleteConfirm(t.id)}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>

                          <ChevronRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-primary transition-colors" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* ── Create/Edit Wizard Dialog ── */}
      <Dialog open={showWizard} onOpenChange={(open) => { if (!open) { setShowWizard(false); resetWizard(); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle data-testid="wizard-title">{editTemplate ? "Edit Compliance" : wizardData.is_library ? "Add to Library" : "New Compliance"}</DialogTitle>
            <DialogDescription>Step {wizardStep + 1} of 2: {wizardStep === 0 ? "Details" : "Select Courses"}</DialogDescription>
          </DialogHeader>

          {wizardStep === 0 ? (
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Title</Label>
                <Input value={wizardData.title} onChange={e => setWizardData(d => ({ ...d, title: e.target.value }))} placeholder="e.g., OSHA 10-Hour General Industry" data-testid="wizard-title-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Description</Label>
                <Textarea value={wizardData.description} onChange={e => setWizardData(d => ({ ...d, description: e.target.value }))} placeholder="Describe the compliance requirements..." className="min-h-[80px]" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Category</Label>
                  <Select value={wizardData.category} onValueChange={v => setWizardData(d => ({ ...d, category: v }))}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Safety", "Quality", "Environmental", "Technical", "Operations", "Maintenance", "HR"].map(c => (
                        <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Days to Complete</Label>
                  <Input type="number" value={wizardData.due_days} onChange={e => setWizardData(d => ({ ...d, due_days: parseInt(e.target.value) || 30 }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Recurrence</Label>
                  <Select value={wizardData.recurrence || "none"} onValueChange={v => setWizardData(d => ({ ...d, recurrence: v === "none" ? "" : v }))}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none" className="text-xs">One-time</SelectItem>
                      <SelectItem value="quarterly" className="text-xs">Quarterly</SelectItem>
                      <SelectItem value="semi_annual" className="text-xs">Semi-Annual</SelectItem>
                      <SelectItem value="annual" className="text-xs">Annual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end pb-1">
                  <div className="flex items-center gap-2">
                    <Switch checked={wizardData.is_mandatory} onCheckedChange={v => setWizardData(d => ({ ...d, is_mandatory: v }))} />
                    <Label className="text-xs">Mandatory</Label>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Search courses..." value={courseSearch} onChange={e => setCourseSearch(e.target.value)} className="pl-9 h-9 text-xs" data-testid="course-search" />
              </div>
              <div className="max-h-56 overflow-y-auto space-y-1 pr-1">
                {courses.filter(c => !courseSearch || c.title.toLowerCase().includes(courseSearch.toLowerCase())).map(c => {
                  const selected = wizardData.course_ids.includes(c.id);
                  return (
                    <div key={c.id} className={`flex items-center gap-2.5 p-2 rounded-lg border cursor-pointer transition-all ${selected ? "border-primary bg-primary/5" : "border-transparent hover:border-muted hover:bg-muted/30"}`}
                      onClick={() => setWizardData(d => ({
                        ...d, course_ids: selected ? d.course_ids.filter(id => id !== c.id) : [...d.course_ids, c.id]
                      }))}>
                      <div className={`w-5 h-5 rounded flex items-center justify-center border-2 shrink-0 ${selected ? "border-primary bg-primary" : "border-muted-foreground/30"}`}>
                        {selected && <CheckCircle2 className="w-3 h-3 text-white" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium truncate">{c.title}</p>
                        <p className="text-[10px] text-muted-foreground">{c.category} · {c.duration_hours || 0}h</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">{wizardData.course_ids.length} course{wizardData.course_ids.length !== 1 ? "s" : ""} selected</p>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            {wizardStep > 0 && <Button variant="outline" onClick={() => setWizardStep(0)}>Back</Button>}
            {wizardStep === 0 ? (
              <Button onClick={() => setWizardStep(1)} disabled={!wizardData.title.trim()} data-testid="wizard-next-btn">Next</Button>
            ) : (
              <Button onClick={handleCreate} data-testid="wizard-save-btn">{editTemplate ? "Update" : "Create"}</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Assignment Modal ── */}
      {showAssign && (
        <AssignmentModal
          open={!!showAssign}
          onOpenChange={() => setShowAssign(null)}
          title={showAssign.title}
          onAssign={async (userIds) => {
            try {
              const res = await api.post(`/compliance/templates/${showAssign.id}/assign`, userIds);
              toast.success(res.data?.message || `Assigned to ${userIds.length} users`);
              fetchAll();
            } catch (e) { toast.error("Assignment failed"); }
          }}
        />
      )}

      {/* ── Delete Confirmation ── */}
      <ConfirmDeleteDialog
        open={!!showDeleteConfirm}
        onOpenChange={(v) => { if (!v) setShowDeleteConfirm(null); }}
        title="Delete Compliance Program"
        description="This will permanently remove this compliance program and all its assignments, tracking data, and learner progress."
        confirmWord="DELETE"
        onConfirm={() => handleDelete(showDeleteConfirm)}
        confirmLabel="Delete Program"
      />

      {/* Share Compliance Template Dialog */}
      <ShareDialog
        open={!!shareTemplate}
        onOpenChange={(v) => { if (!v) setShareTemplate(null); }}
        itemType="compliance"
        itemId={shareTemplate?.id || ""}
        itemTitle={shareTemplate?.title || ""}
      />
    </AdminLayout>
  );
};

export default CompliancePage;
